import Decimal from 'decimal.js';

// Configure Decimal.js for percentage calculations
Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP
});

/**
 * Default scale for percentage values (2 decimal places)
 */
const DEFAULT_SCALE = 2;

/**
 * Percent class for safe arithmetic operations on percentage values.
 * Uses decimal.js internally to avoid floating-point precision issues.
 *
 * @example
 * const percent = Percent.from(7);
 * console.log(percent.format()); // '7%'
 * console.log(percent.toString()); // '7.00'
 *
 * const percent2 = Percent.from('7.5');
 * console.log(percent2.isValid()); // true (0-100 range)
 */
export class Percent {
  private readonly value: Decimal;
  private readonly scale: number;

  private constructor(value: Decimal, scale: number = DEFAULT_SCALE) {
    this.value = value;
    this.scale = scale;
  }

  /**
   * Create a Percent instance from a string, number, or another Percent
   */
  static from(value: string | number | Percent, scale: number = DEFAULT_SCALE): Percent {
    if (value instanceof Percent) {
      return value;
    }
    const decimal = new Decimal(value);
    return new Percent(decimal, scale);
  }

  /**
   * Create a Percent instance representing zero
   */
  static zero(scale: number = DEFAULT_SCALE): Percent {
    return new Percent(new Decimal(0), scale);
  }

  /**
   * Get the maximum of multiple Percent values
   */
  static max(...values: Percent[]): Percent {
    if (values.length === 0) {
      throw new Error('Cannot get max of empty array');
    }
    return values.reduce((max, val) => (val.gt(max) ? val : max));
  }

  /**
   * Get the minimum of multiple Percent values
   */
  static min(...values: Percent[]): Percent {
    if (values.length === 0) {
      throw new Error('Cannot get min of empty array');
    }
    return values.reduce((min, val) => (val.lt(min) ? val : min));
  }

  // ============================================
  // Arithmetic Operations
  // ============================================

  /**
   * Add another Percent value
   */
  add(other: Percent | string | number): Percent {
    const otherPercent = Percent.from(other, this.scale);
    return new Percent(this.value.plus(otherPercent.value), this.scale);
  }

  /**
   * Subtract another Percent value
   */
  subtract(other: Percent | string | number): Percent {
    const otherPercent = Percent.from(other, this.scale);
    return new Percent(this.value.minus(otherPercent.value), this.scale);
  }

  // ============================================
  // Comparison Operations
  // ============================================

  /**
   * Check if equal to another Percent value
   */
  eq(other: Percent | string | number): boolean {
    const otherPercent = Percent.from(other, this.scale);
    return this.value.equals(otherPercent.value);
  }

  /**
   * Check if greater than another Percent value
   */
  gt(other: Percent | string | number): boolean {
    const otherPercent = Percent.from(other, this.scale);
    return this.value.greaterThan(otherPercent.value);
  }

  /**
   * Check if greater than or equal to another Percent value
   */
  gte(other: Percent | string | number): boolean {
    const otherPercent = Percent.from(other, this.scale);
    return this.value.greaterThanOrEqualTo(otherPercent.value);
  }

  /**
   * Check if less than another Percent value
   */
  lt(other: Percent | string | number): boolean {
    const otherPercent = Percent.from(other, this.scale);
    return this.value.lessThan(otherPercent.value);
  }

  /**
   * Check if less than or equal to another Percent value
   */
  lte(other: Percent | string | number): boolean {
    const otherPercent = Percent.from(other, this.scale);
    return this.value.lessThanOrEqualTo(otherPercent.value);
  }

  /**
   * Check if this value is zero
   */
  isZero(): boolean {
    return this.value.isZero();
  }

  /**
   * Check if this value is positive (> 0)
   */
  isPositive(): boolean {
    return this.value.isPositive() && !this.value.isZero();
  }

  /**
   * Check if this value is negative (< 0)
   */
  isNegative(): boolean {
    return this.value.isNegative();
  }

  // ============================================
  // Validation
  // ============================================

  /**
   * Check if this percentage is valid (0-100 range by default)
   * @param min - Minimum valid percentage (default: 0)
   * @param max - Maximum valid percentage (default: 100)
   */
  isValid(min: number = 0, max: number = 100): boolean {
    return this.value.gte(min) && this.value.lte(max);
  }

  /**
   * Check if this percentage is within a custom range
   */
  isInRange(min: Percent | number, max: Percent | number): boolean {
    return this.gte(min) && this.lte(max);
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Clamp value between min and max
   */
  clamp(min: Percent | string | number, max: Percent | string | number): Percent {
    const minPercent = Percent.from(min, this.scale);
    const maxPercent = Percent.from(max, this.scale);

    if (this.lt(minPercent)) return minPercent;
    if (this.gt(maxPercent)) return maxPercent;
    return this;
  }

  /**
   * Convert to a JavaScript number
   */
  toNumber(): number {
    return this.value.toDecimalPlaces(this.scale, Decimal.ROUND_HALF_UP).toNumber();
  }

  /**
   * Convert to a string with the configured scale (for database storage)
   * Always includes decimal places for consistency
   */
  toString(): string {
    return this.value.toDecimalPlaces(this.scale, Decimal.ROUND_HALF_UP).toFixed(this.scale);
  }

  /**
   * Format as a display string with % symbol
   * For whole numbers, omits the decimal places
   */
  format(options?: {symbol?: boolean}): string {
    const {symbol = true} = options ?? {};
    const num = this.toNumber();
    const isWholeNumber = Number.isInteger(num);
    const formatted = isWholeNumber ? num.toString() : this.toString();
    return symbol ? `${formatted}%` : formatted;
  }

  /**
   * Alias for toString for JSON serialization
   */
  toJSON(): string {
    return this.toString();
  }
}

// ============================================
// Convenience Functions
// ============================================

/**
 * Parse a value to a Percent instance
 * Returns zero for null/undefined/empty string
 */
export function parsePercent(value: string | number | Percent | null | undefined): Percent {
  if (value === null || value === undefined || value === '') {
    return Percent.zero();
  }
  if (value instanceof Percent) {
    return value;
  }
  return Percent.from(value);
}

/**
 * Format a percentage value for display
 * Convenience function for Percent.format()
 */
export function formatPercent(value: string | number | Percent | null | undefined): string {
  const percent = parsePercent(value);
  return percent.format();
}

/**
 * Zero constant for convenience
 */
export const ZERO_PERCENT = Percent.zero();
